import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { useNutritionBasis } from "#/composables/useNutritionBasis";
import { useCardStore } from "#/store/card";
import { useThemeStore } from "#/store/theme";
import { formatPrice } from "#/utils/i18next";
import { calculateProductNutritions, emptyNutritions, formatNutritionWeight, hasVisibleMacroNutritions, hasVisibleWeight } from "#/utils/nutritions";

export function useProductNutritions() {
	const cardStore = useCardStore();
	const themeStore = useThemeStore();
	const { t } = useTranslation();

	const portion = computed(() => {
		const product = cardStore.stateCard.openCard;
		if (!product) return emptyNutritions();

		return calculateProductNutritions({
			product,
			selectedSize: cardStore.stateCard.selectedSize,
			groupModifiersSelected: cardStore.stateCard.groupModifiersSelected,
			amount: product.amount ?? 1,
		});
	});

	const { hasBasisToggle, nutritions } = useNutritionBasis(portion);

	const showNutritionsInCard = computed(() => !!themeStore.data?.show_nutrition_in_product_card);
	const hasMacroNutritions = computed(() => showNutritionsInCard.value && hasVisibleMacroNutritions(nutritions.value));
	const hasWeight = computed(() => showNutritionsInCard.value && hasVisibleWeight(nutritions.value));
	const hasNutritions = computed(() => hasMacroNutritions.value || hasWeight.value);
	const weightLabel = computed(() =>
		formatNutritionWeight(nutritions.value, (value, unit) =>
			formatPrice(value, unit === "gram" ? t(`card.nutritional.unit.gram`) : t(`card.nutritional.unit.milliliter`)),
		),
	);

	return {
		portion,
		nutritions,
		hasBasisToggle,
		weightLabel,
		showNutritionsInCard,
		hasMacroNutritions,
		hasWeight,
		hasNutritions,
	};
}
